import { useCallback, useContext } from 'react';
import {
  GetGeneralPageApiRequestParams,
  GetGeneralPageApiResponse,
} from '@src/@types/cms';
import { AnalyticsUserContext } from '@src/contexts/analytics-user-context';
import { useFetch } from '@src/hooks/use-fetch.hook';
import CookiesService, { CookieNames } from '@src/services/cookies.service';

export const useCmsApi = () => {
  const fetch = useFetch();

  const {
    loaded: analyticsUserContextLoaded,
    error: analyticsUserContextError,
    analyticsUserContextResponse,
  } = useContext(AnalyticsUserContext);

  const isReady = Boolean(
    analyticsUserContextLoaded || analyticsUserContextError,
  );

  /**
   * Designed to use in conjunction with ready as this function
   * relies on data from {@link AnalyticsUserContext} to be available.
   * Consumers should utilize an effect lifecycle method to only
   * invoke getGeneralPage if ready is truthy and delcare both ready and
   * getGeneralPage as dependencies.
   */
  const getGeneralPage = useCallback(
    async (
      args: GetGeneralPageApiRequestParams,
    ): Promise<GetGeneralPageApiResponse> => {
      // Throw error if getGeneralPage was invoked before analytics
      // user context was ready.
      if (!isReady) {
        throw new Error(
          'CMS getGeneralPage invoked before analytics user context was ready.',
        );
      }

      const chewyAuthIdToken = CookiesService.getCookie(CookieNames.TICKET);

      const { userContextData } = analyticsUserContextResponse;
      const { personalizationId } = userContextData;

      const urlSearchParams = new URLSearchParams();
      if (args?.selectionKey != null) {
        urlSearchParams.append('selectionKey', args.selectionKey);
      }
      if (args?.forceRouteTo != null) {
        urlSearchParams.append('forceRouteTo', args.forceRouteTo);
      }
      if (args?.variantData != null) {
        urlSearchParams.append('variantData', args.variantData);
      }
      if (args?.shouldBeVerbose != null) {
        urlSearchParams.append('shouldBeVerbose', String(args.shouldBeVerbose));
      }
      if (args?.shouldExplain != null) {
        urlSearchParams.append('shouldExplain', String(args.shouldExplain));
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (chewyAuthIdToken) {
        headers['X-Chewy-Auth-Id-Token'] = chewyAuthIdToken;
      }
      if (personalizationId) {
        headers['X-Personalization-Id'] = personalizationId;
      }

      const res = await fetch(
        `/api/content/v1/pages/general/types/${
          args?.generalType ?? 'pet-insurance'
        }?${urlSearchParams}`,
        {
          method: 'GET',
          headers,
        },
        {
          loginRedirect: false,
        },
      );

      if (!res?.ok) {
        throw new Error(
          `Error getting CMS page. ${res?.statusText ?? 'Empty response.'}`,
        );
      }

      return res.json();
    },
    [isReady, fetch],
  );

  return { isReady, getGeneralPage };
};
